import React, {Dispatch, useCallback, useEffect, useMemo} from "react"
import {IBrigade} from "../../../../models/brigade"
import {useAppDispatch, useAppSelector} from "../../../../hooks/redux"
import {useFormik} from "formik"
import Modal from "../Modal"
import {
  createBrigade,
  fetchBrigades, setCurrentPage,
  updateBrigade
} from "../../../../store/actions/brigadeActions"
import Input from "../../../generic/dataEntry/Input/Input"
import {brigadeSlice} from "../../../../store/slices/brigadeSlice"
import classes from "../Modal/Modal.module.scss"
import {CustomSelectOption} from "../../../../models"
import CustomSelect from "../../../generic/dataEntry/CustomSelect/CustomSelect"
import IconButton from "../../../generic/buttons/IconButton"
import Button from "../../../generic/buttons/Button/Button"
import {fetchEmployees} from "../../../../store/actions/employeeActions"

interface IEditBrigadeModalProps {
  close: any
  children?: string | JSX.Element | JSX.Element[]
  editSuccess: Dispatch<any>
  editableItem?: IBrigade
}

function ModalEditBrigade({close, editSuccess, editableItem}: IEditBrigadeModalProps) {

  const dispatch = useAppDispatch()
  const { fieldError, pageSize, currentPage, isCreating, isUpdating, employeeGroupError } = useAppSelector(state => state.brigade)
  const employees = useAppSelector(state => state.employee.list)
  const modalTitle = editableItem ? "Редактирование бригады" : "Создание бригады"

  useEffect(() => {
    dispatch(fetchEmployees({page: 1, pageSize: 0, role_id: 3}))
  }, [dispatch])

  const employeeOptions = useMemo(() => {
    return employees && employees.map(employee => (
        {
          label: employee.fio,
          value: employee.user_uuid
        }
      ))
  }, [employees])

  const employeeIDs = editableItem ? editableItem.employees.map(employee => (employee.id)) : []

  const initialFormValues =
    editableItem ? {
      brigade_id: editableItem.id,
      brigade: editableItem.brigade,
      employees: employeeIDs
    } : {
      brigade: "",
      employees: []
    }

  const {values, touched, handleChange, handleReset} = useFormik({
    initialValues: initialFormValues,
    onSubmit: () => {
    }
  })

  const addNewEmployee = () => {
    const employeeArr = [...values.employees, '']
    handleChange({target:{name:"employees",value: employeeArr}})
  }

  const deleteEmployee = useCallback((id: string) => {
    const newArr = values.employees.filter((elem) => (elem !== id))
    handleChange({target:{name:"employees", value: newArr}})
  }, [handleChange, values.employees])

  const handleChangeEmployee = useCallback((id: number, value: string) => {

    let employees = [...values.employees]
    employees.splice(id,1, value)
    const uniqueEmployees = [...new Set(employees)]
    handleChange({target: {name: "employees", value: uniqueEmployees}})

  }, [handleChange, values.employees])

  const employeesFieldsTemplate = useMemo(() => {
    return values.employees.map((employeeID, id) => {

      let valid = true
      let errorMessage = ''

      if (employeeGroupError?.uuids && employeeGroupError?.uuids.includes(employeeID)) {
        valid = false
        errorMessage = 'Сотрудник в другой бригаде'
      }

      return (
        <div className={classes.employee_field} key={employeeID}>
          <div className={classes.employee_field_select}>
            <CustomSelect
              name={'employees'}
              label={'ФИО сотрудника'}
              options={employeeOptions}
              placeholder={"Выбрать..."}
              value={employeeOptions.find(item => item.value === employeeID)}
              valid={valid}
              errorMessage={errorMessage}
              shouldValidate
              onChange={(option: CustomSelectOption) => {
                return handleChangeEmployee(id, option.value)
              }}
            />
          </div>
          <div className={classes.employee_field_delete}>
            <IconButton onClick={() => deleteEmployee(employeeID)} icon={"trash"}/>
          </div>
        </div>
      )
    })
  }, [values.employees, employeeOptions, handleChangeEmployee, deleteEmployee, employeeGroupError])

  const inputChangeHandler = (e: React.ChangeEvent) => {
    handleChange(e)
    const fieldName: any = e.target.getAttribute('name')

    if (fieldError[fieldName]) {
      dispatch(brigadeSlice.actions.clearFieldError(fieldName))
    }
  }

  const saveHandler = async () => {
    const brigadeID = await dispatch(createBrigade(values))

    if (brigadeID) {
      dispatch(setCurrentPage(1))
      await dispatch(fetchBrigades({page: 1, pageSize}))
      close()
      editSuccess(true)
      handleReset(initialFormValues)
    }
  }

  const updateHandler = async () => {
    const isUpdated = await dispatch(updateBrigade(values))

    if (isUpdated) {
      editSuccess(true)
      dispatch(setCurrentPage(1))
      await dispatch(fetchBrigades({page: 1, pageSize}))
      close()
      handleReset(initialFormValues)
    }
  }

  const closeHandler = () => {
    close()
    dispatch(brigadeSlice.actions.clearAllErrors())
  }

  return (
    <Modal
      title={modalTitle}
      close={closeHandler}
      dialogSizeSuffix={"w435"}
      submitClick={editableItem ? updateHandler : saveHandler}
      cancelClick={closeHandler}
      submitDisabled={isUpdating || isCreating}
      submitLoading={isUpdating || isCreating}
    >
      <form className={classes.form}>
        <div className={classes.form_field}>
          <Input
            value={values.brigade}
            onChange={(e: React.ChangeEvent) => {
              inputChangeHandler(e)
            }}
            name={"brigade"}
            label={"Название бригады"}
            valid={!fieldError.brigade}
            shouldValidate
            errorMessage={fieldError.brigade}
            touched={touched.brigade}
            required
          />
        </div>
        <div className={classes.form_employees}>
          {employeesFieldsTemplate}
          <Button
            onClick={addNewEmployee}
            color={"white"}
            className={classes.button_add_employee}
          >
            + Прикрепить сотрудника
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default ModalEditBrigade